import { ReactNode } from 'react';
import { ImageOutlined } from '@mui/icons-material';
import { Checkbox, FormGroup, Radio, RadioGroup } from '@mui/material';
import { answeredProductSap, modelChoiceOf, selectChoiceOf } from './flow';
import { useProductImage } from './productApi';
import {
  BlockHeader,
  BlockLead,
  BlockMeta,
  BlockTail,
  ErrorText,
  OptionControl,
  OptionImage,
  OptionsRow,
  QuestionCard,
  QuestionTitle,
  TextAction,
  TreeChildren,
  TreeNode,
} from './styles';
import { Answers, BasketQuestionDTO, ModelChoices } from './types';

type Props = {
  question: BasketQuestionDTO;
  answers: Answers;
  models: ModelChoices;
  showError: boolean;
  blockRef: (element: HTMLDivElement | null) => void;
  onAnswer: (value: string) => void;
  /**
   * Alternativa que pede a escolha de um modelo: abre o pop-up em vez de
   * responder. Sem ele (prévia da configuração), vira uma resposta comum.
   */
  onPickModel?: (optionValue: string) => void;
  /** Produto escolhido no card do balaio. Sem ele, o card não aparece. */
  onSelectProduct?: (productId: number) => void;
  /** Dentro de um bloco: card interno, ligado à pergunta mãe pela árvore. */
  grouped?: boolean;
  /** Antes do título (número do bloco) e depois dele (check de concluído). */
  leading?: ReactNode;
  trailing?: ReactNode;
};

/** Foto do produto da alternativa escolhida; ícone neutro sem produto. */
const AnsweredImage = ({ sapCode }: { sapCode?: string }) => {
  const src = useProductImage(sapCode);
  return (
    <OptionImage aria-hidden="true">
      {src ? <img src={src} alt="" /> : <ImageOutlined />}
    </OptionImage>
  );
};

/**
 * Card de pergunta, sempre aberto. Clicar numa alternativa só grava a escolha:
 * nada avança nem fecha. Ao lado, a foto do produto que a escolha adiciona.
 */
export const QuestionBlockView = ({
  question,
  answers,
  models,
  showError,
  blockRef,
  onAnswer,
  onPickModel,
  onSelectProduct,
  grouped,
  leading,
  trailing,
}: Props) => {
  const value = answers[question.code];
  const isConfirmation = question.type === 'BOOLEAN';
  const answeredOption = question.options.find((item) => item.value === value);
  const modalBasket = answeredOption && modelChoiceOf(answeredOption);
  const selectBasket = answeredOption && selectChoiceOf(answeredOption);
  const chosenId = models[question.code];
  const asksModel = (optionValue: string) => {
    const option = question.options.find((item) => item.value === optionValue);
    return Boolean(onPickModel && option && modelChoiceOf(option));
  };

  return (
    <>
      <QuestionCard
        ref={blockRef}
        invalid={showError && !selectBasket}
        grouped={grouped}
      >
        <BlockHeader>
          <BlockLead>
            {leading}
            <QuestionTitle>{question.text}</QuestionTitle>
          </BlockLead>
          {(!question.required || trailing) && (
            <BlockTail>
              {!question.required && <BlockMeta>Opcional</BlockMeta>}
              {trailing}
            </BlockTail>
          )}
        </BlockHeader>

        <OptionsRow>
          <div>
            {isConfirmation ? (
              <FormGroup>
                <OptionControl
                  selected={value === 'true'}
                  control={
                    <Checkbox
                      size="small"
                      checked={value === 'true'}
                      onChange={(event) =>
                        onAnswer(event.target.checked ? 'true' : '')
                      }
                    />
                  }
                  label={<strong>Estou ciente</strong>}
                />
              </FormGroup>
            ) : (
              <RadioGroup
                name={question.code}
                value={value ?? ''}
                onChange={(event) => {
                  // Alternativa com modelo: o pop-up cuida, a resposta só vale depois dele.
                  if (!asksModel(event.target.value))
                    onAnswer(event.target.value);
                }}
              >
                {question.options.map((option) => {
                  return (
                    <OptionControl
                      key={option.value}
                      value={option.value}
                      selected={value === option.value}
                      control={<Radio size="small" />}
                      // Reabre o pop-up também ao clicar de novo na alternativa já
                      // marcada (o onChange não dispara). O label gera dois cliques;
                      // só o do próprio input conta.
                      onClick={(event) => {
                        const isInput =
                          (event.target as HTMLElement).tagName === 'INPUT';
                        if (isInput && asksModel(option.value)) {
                          onPickModel?.(option.value);
                        }
                      }}
                      label={<strong>{option.label}</strong>}
                    />
                  );
                })}
              </RadioGroup>
            )}

            {modalBasket && chosenId !== undefined && onPickModel && (
              <TextAction onClick={() => onPickModel(value)} sx={{ mt: 1 }}>
                Alterar modelo
              </TextAction>
            )}
          </div>

          {!isConfirmation && (
            <AnsweredImage
              sapCode={answeredProductSap(question, answers, models)}
            />
          )}
        </OptionsRow>

        {showError && !selectBasket && (
          <ErrorText role="alert">Responda para prosseguir.</ErrorText>
        )}
      </QuestionCard>
      {selectBasket && onSelectProduct && (
        <TreeChildren indent={Boolean(grouped)}>
          <TreeNode nested lined={Boolean(grouped)}>
            <QuestionCard grouped={grouped} invalid={showError}>
              <BlockHeader>
                <QuestionTitle>Escolha o produto</QuestionTitle>
              </BlockHeader>
              <OptionsRow>
                <RadioGroup
                  name={`${question.code}-product`}
                  aria-label={`Produtos para ${question.text}`}
                  value={chosenId ?? ''}
                  onChange={(event) =>
                    onSelectProduct(Number(event.target.value))
                  }
                >
                  {selectBasket.products.map((item) => (
                    <OptionControl
                      key={item.id}
                      value={item.id}
                      selected={chosenId === item.id}
                      control={<Radio size="small" />}
                      label={<strong>{item.description}</strong>}
                    />
                  ))}
                </RadioGroup>
                <AnsweredImage
                  sapCode={
                    selectBasket.products.find((item) => item.id === chosenId)
                      ?.sap_code
                  }
                />
              </OptionsRow>
              {showError && (
                <ErrorText role="alert">
                  Selecione um produto para prosseguir.
                </ErrorText>
              )}
            </QuestionCard>
          </TreeNode>
        </TreeChildren>
      )}
    </>
  );
};
